/**
 * FundElite portal branding assets.
 *
 * Self Repair / Sign In / Sign Up: green favicon + theme-aware wordmarks.
 * Agency Repair / Agency Specialist: gold favicon + gold wordmark.
 * FundGenius HQ must never consume these via FundEliteBrandMark guards.
 */

export const FUNDELITE_BRAND = {
  name: 'FundElite',
  shortName: 'FundElite',
  description: 'FundElite Credit and Funding Platform',
  domain: 'app.fundelite.ai',
  themeColor: '#0B2B22',
  backgroundColor: '#061A16',
};

/** Light-mode / light-background Self Repair wordmark (Logo3). */
export const SELF_REPAIR_LOGO_LIGHT_BACKGROUND =
  '/branding/self-repair/fundelite-logo-light-background.png';

/** Dark-mode / dark-background Self Repair wordmark (Logo4). */
export const SELF_REPAIR_LOGO_DARK_BACKGROUND =
  '/branding/self-repair/fundelite-logo-dark-background.png';

/** Green FundElite app icon / favicon (Self Repair + auth). */
export const SELF_REPAIR_FAVICON = '/branding/self-repair/fundelite-favicon.png';

export const SELF_REPAIR_BRAND_ASSETS = {
  favicon: SELF_REPAIR_FAVICON,
  icon: SELF_REPAIR_FAVICON,
  logoLight: SELF_REPAIR_LOGO_LIGHT_BACKGROUND,
  logoDark: SELF_REPAIR_LOGO_DARK_BACKGROUND,
  /** @deprecated prefer theme-resolved logo via getSelfRepairLogoForTheme */
  logo: SELF_REPAIR_LOGO_DARK_BACKGROUND,
  logoAlt: SELF_REPAIR_LOGO_LIGHT_BACKGROUND,
};

export const AGENCY_ADMIN_BRAND_ASSETS = {
  favicon: '/branding/agency/fundelite-agency-favicon.png',
  icon: '/branding/agency/fundelite-agency-favicon.png',
  logo: '/branding/agency/fundelite-agency-logo.png',
};

/** @typedef {'self-repair' | 'agency-admin'} FundEliteBrandVariant */
/** @typedef {'light' | 'dark'} FundEliteBrandTheme */

/**
 * Resolve Self Repair / auth wordmark from app theme state.
 * @param {FundEliteBrandTheme | string} [theme]
 */
export function getSelfRepairLogoForTheme(theme = 'dark') {
  return theme === 'light'
    ? SELF_REPAIR_LOGO_LIGHT_BACKGROUND
    : SELF_REPAIR_LOGO_DARK_BACKGROUND;
}

/**
 * Read resolved light/dark from the document theme hooks already applied
 * by ThemeProvider / SelfServe ThemeProvider (class + data-theme).
 * @returns {FundEliteBrandTheme}
 */
export function readDocumentBrandTheme() {
  if (typeof document === 'undefined') return 'dark';
  const root = document.documentElement;
  if (root.classList.contains('light')) return 'light';
  if (root.classList.contains('dark')) return 'dark';
  const dataTheme = root.getAttribute('data-theme');
  if (dataTheme === 'light' || dataTheme === 'dark') return dataTheme;
  return 'dark';
}

/**
 * @param {FundEliteBrandVariant} variant
 * @param {{ theme?: FundEliteBrandTheme | string }} [options]
 */
export function getFundEliteBrandAssets(variant = 'self-repair', options = {}) {
  if (variant === 'agency-admin') {
    return AGENCY_ADMIN_BRAND_ASSETS;
  }
  const theme = options.theme === 'light' || options.theme === 'dark'
    ? options.theme
    : readDocumentBrandTheme();
  return {
    ...SELF_REPAIR_BRAND_ASSETS,
    logo: getSelfRepairLogoForTheme(theme),
  };
}
